import React, { useState, useMemo } from "react";
import { X, ChevronLeft, ChevronRight, Search } from "lucide-react";
import {
  Files,
  FolderOpen,
  Tag,
  User,
  AlertCircle,
  LucideIcon,
} from "lucide-react";

interface Article {
  id: string;
  title: string;
  content: string;
}

interface Category {
  id: string;
  name: string;
  icon: LucideIcon;
  articles: Article[];
}

const helpData: Category[] = [
  {
    id: "getting-started",
    name: "Getting Started",
    icon: Files,
    articles: [
      {
        id: "1",
        title: "How do I create an account?",
        content:
          "To create an account, click the 'Sign Up' button on the homepage and follow the registration process. You'll need to provide your email address and create a password.\n\nOnce you've entered your details, you'll receive a verification email. Click the link in that email to activate your account and start using the platform.",
      },
      {
        id: "2",
        title: "How do I upload documents?",
        content:
          "Navigate to the Documents page and click the 'New document' button in the sidebar. You can drag and drop files or select them from your computer.\n\n**Supported formats include:**\n- PDF documents\n- DOC and DOCX files\n- Images (PNG, JPG, JPEG)\n\nYour documents will be processed automatically and you'll be notified when they're ready to view.",
      },
      {
        id: "3",
        title: "What file types are supported?",
        content:
          "We support a wide range of file types to help you manage all your documents in one place.\n\n**Document formats:**\n- PDF (.pdf)\n- Microsoft Word (.doc, .docx)\n- Text files (.txt)\n\n**Image formats:**\n- PNG (.png)\n- JPEG (.jpg, .jpeg)\n\nFile size limits vary by account type. Free accounts can upload files up to 10 pages per day.",
      },
    ],
  },
  {
    id: "documents",
    name: "Document Management",
    icon: FolderOpen,
    articles: [
      {
        id: "4",
        title: "How do I organise my documents?",
        content:
          "You can organise documents using labels. Create custom labels from the sidebar by clicking 'New label' and apply them to your documents for easy categorisation and filtering.\n\n**To add a label to a document:**\n1. Open the document or select it from the list\n2. Click the label icon or select 'Add labels'\n3. Choose one or more labels to apply\n\nYou can filter your documents by label using the sidebar navigation.",
      },
      {
        id: "5",
        title: "Can I share documents with others?",
        content:
          "Yes! You can share documents with other users or via public links.\n\n**To share a document:**\n1. Open the document you want to share\n2. Click the 'Share' button\n3. Enter the email addresses of people you want to share with, or generate a shareable link\n\nYou can control permissions for each person you share with.",
      },
      {
        id: "6",
        title: "How do I delete a document?",
        content:
          "Navigate to the Documents page, find the document you want to delete, click the three-dot menu, and select 'Delete'. Confirm the deletion when prompted.\n\n**Important:** Deleted documents cannot be recovered. Make sure you have a backup if needed before deleting.",
      },
    ],
  },
  {
    id: "tags",
    name: "Labels & Organisation",
    icon: Tag,
    articles: [
      {
        id: "7",
        title: "How do I create a label?",
        content:
          "In the sidebar, look for the 'ORGANISE DOCUMENTS' section and click 'New label'. Enter a name for your label and choose a colour.\n\n**Tips for effective labelling:**\n- Use clear, descriptive names\n- Create a consistent naming convention\n- Use colours to quickly identify different types of documents",
      },
      {
        id: "8",
        title: "Can I edit or delete labels?",
        content:
          "Yes! Hover over any label in the sidebar to reveal the three-dot menu. From there you can:\n\n- **Edit:** Change the label name or colour\n- **Delete:** Remove the label entirely\n\nWhen you delete a label, it's removed from all associated documents, but the documents themselves remain intact.",
      },
      {
        id: "9",
        title: "What happens to documents when I delete a label?",
        content:
          "When you delete a label, it's removed from all associated documents, but the documents themselves remain intact and accessible.\n\nThe documents will move to the 'Uncategorised' section if they don't have any other labels applied.",
      },
    ],
  },
  {
    id: "account",
    name: "Account & Settings",
    icon: User,
    articles: [
      {
        id: "10",
        title: "How do I change my password?",
        content:
          "Go to your account settings by clicking on your profile or navigating to Settings.\n\n**To change your password:**\n1. Go to Settings > Security\n2. Click 'Change Password'\n3. Enter your current password\n4. Enter and confirm your new password\n5. Click 'Update Password'\n\nFor security, choose a strong password with a mix of letters, numbers, and symbols.",
      },
      {
        id: "11",
        title: "How do I update my profile?",
        content:
          "Navigate to your Profile page by clicking on your name in the sidebar or going to Settings.\n\n**You can update:**\n- Your display name\n- Profile picture\n- Email preferences\n- Notification settings",
      },
      {
        id: "12",
        title: "How do I delete my account?",
        content:
          "If you wish to delete your account, please contact our support team through the Contact Us form. We'll assist you with the account deletion process.\n\n**Before deleting your account:**\n- Download any documents you want to keep\n- Cancel any active subscriptions\n- Note that account deletion is permanent and cannot be undone",
      },
    ],
  },
  {
    id: "troubleshooting",
    name: "Troubleshooting",
    icon: AlertCircle,
    articles: [
      {
        id: "13",
        title: "My document upload is failing. What should I do?",
        content:
          "If your document upload is failing, try these steps:\n\n**1. Check your internet connection**\nMake sure you have a stable internet connection.\n\n**2. Verify file size and format**\nEnsure your file is under the size limit and in a supported format.\n\n**3. Clear your browser cache**\nSometimes cached data can cause issues.\n\n**4. Try a different browser**\nIf the problem persists, try uploading from a different browser.\n\nIf none of these work, please contact our support team with details about the error.",
      },
      {
        id: "14",
        title: "I can't see my documents. Where are they?",
        content:
          "If you can't see your documents, try these steps:\n\n**1. Check you're logged in correctly**\nMake sure you're signed in to the right account.\n\n**2. Check your filters**\nYou might have a label filter active. Click 'All documents' in the sidebar.\n\n**3. Refresh the page**\nSometimes a simple refresh resolves display issues.\n\n**4. Clear browser cache**\nCached data can sometimes cause problems.\n\nIf documents are still missing after these steps, please contact support immediately.",
      },
      {
        id: "15",
        title: "The app is running slowly. How can I fix this?",
        content:
          "If the app is running slowly, try these optimisation steps:\n\n**1. Clear your browser cache**\nOld cached files can slow things down.\n\n**2. Close unnecessary tabs**\nToo many open tabs can affect performance.\n\n**3. Try a different browser**\nSome browsers perform better than others.\n\n**4. Check your internet speed**\nA slow connection can affect app performance.\n\n**5. Disable browser extensions**\nSome extensions can interfere with web apps.\n\nIf performance issues persist, please contact our support team.",
      },
    ],
  },
];

type View = "categories" | "articles" | "article";

interface HelpCentreModalProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  onContactClick?: () => void;
}

const HelpCentreModal: React.FC<HelpCentreModalProps> = ({
  isOpen,
  setIsOpen,
  onContactClick,
}) => {
  const [view, setView] = useState<View>("categories");
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    null,
  );
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const totalArticles = useMemo(() => {
    return helpData.reduce((sum, cat) => sum + cat.articles.length, 0);
  }, []);

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return null;

    const query = searchQuery.toLowerCase();
    const results: { category: Category; article: Article }[] = [];

    helpData.forEach((category) => {
      category.articles.forEach((article) => {
        if (
          article.title.toLowerCase().includes(query) ||
          article.content.toLowerCase().includes(query)
        ) {
          results.push({ category, article });
        }
      });
    });

    return results;
  }, [searchQuery]);

  const handleCategoryClick = (category: Category) => {
    setSelectedCategory(category);
    setView("articles");
    setSearchQuery("");
  };

  const handleArticleClick = (article: Article, category?: Category) => {
    setSelectedArticle(article);
    if (category) {
      setSelectedCategory(category);
    }
    setView("article");
    setSearchQuery("");
  };

  const handleBack = () => {
    if (view === "article") {
      setView("articles");
      setSelectedArticle(null);
    } else if (view === "articles") {
      setView("categories");
      setSelectedCategory(null);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    // Reset state after close animation
    setTimeout(() => {
      setView("categories");
      setSelectedCategory(null);
      setSelectedArticle(null);
      setSearchQuery("");
    }, 200);
  };

  const getTitle = () => {
    if (searchQuery.trim()) return "Search Results";
    if (view === "article" && selectedArticle) return selectedArticle.title;
    if (view === "articles" && selectedCategory) return selectedCategory.name;
    return "Help centre";
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-4 sm:inset-auto sm:bottom-4 sm:right-4 z-50">
      {/* modal */}
      <div className="bg-white rounded-lg shadow-2xl w-full h-full sm:w-[380px] sm:h-[550px] flex flex-col overflow-hidden border border-gray-200">
        {/* header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <button
            onClick={handleBack}
            className={`p-1 rounded hover:bg-gray-100 transition-colors ${
              view === "categories" && !searchQuery.trim()
                ? "invisible"
                : "visible"
            }`}
          >
            <ChevronLeft size={20} className="text-gray-600" />
          </button>

          <h2 className="text-lg font-semibold text-gray-900 flex-1 text-center truncate px-2">
            {getTitle()}
          </h2>

          <button
            onClick={handleClose}
            className="p-1 rounded hover:bg-gray-100 transition-colors"
          >
            <X size={20} className="text-gray-600" />
          </button>
        </div>

        {/* search*/}
        {view !== "article" && (
          <div className="px-4 py-3 border-b border-gray-200">
            <div className="relative">
              <input
                type="text"
                placeholder="Search for help"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
              <Search
                size={18}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-500"
              />
            </div>
          </div>
        )}

        {/* content */}
        <div className="flex-1 overflow-y-auto">
          {/* results */}
          {searchQuery.trim() && searchResults && (
            <div className="p-4">
              {searchResults.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  No results found for "{searchQuery}"
                </p>
              ) : (
                <div className="space-y-1">
                  {searchResults.map(({ category, article }) => (
                    <button
                      key={article.id}
                      onClick={() => handleArticleClick(article, category)}
                      className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {article.title}
                        </p>
                        <p className="text-xs text-gray-500">{category.name}</p>
                      </div>
                      <ChevronRight size={18} className="text-blue-500 ml-2" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* categories */}
          {!searchQuery.trim() && view === "categories" && (
            <div className="p-4">
              <div className="mb-4 pb-4 border-b border-gray-200">
                <h3 className="text-lg font-bold text-gray-900">
                  Data Revival
                </h3>
                <p className="text-sm text-gray-500">
                  {totalArticles} articles
                </p>
              </div>

              <div className="space-y-1">
                {helpData.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => handleCategoryClick(category)}
                    className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {category.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {category.articles.length} articles
                      </p>
                    </div>
                    <ChevronRight size={18} className="text-blue-500" />
                  </button>
                ))}
              </div>

              {/* contact us section */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600 mb-2">
                  Didn't find what you're looking for?
                </p>
                <button
                  onClick={() => {
                    handleClose();
                    onContactClick?.();
                  }}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  Contact us
                </button>
              </div>
            </div>
          )}

          {/* articles */}
          {!searchQuery.trim() && view === "articles" && selectedCategory && (
            <div className="p-4">
              <div className="mb-4 pb-4 border-b border-gray-200">
                <h3 className="text-lg font-bold text-gray-900">
                  {selectedCategory.name}
                </h3>
                <p className="text-sm text-gray-500">
                  {selectedCategory.articles.length} articles
                </p>
              </div>

              <div className="space-y-1">
                {selectedCategory.articles.map((article) => (
                  <button
                    key={article.id}
                    onClick={() => handleArticleClick(article)}
                    className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <p className="text-sm text-gray-900 flex-1 pr-2">
                      {article.title}
                    </p>
                    <ChevronRight size={18} className="text-blue-500" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {view === "article" && selectedArticle && (
            <div className="p-4">
              <div className="mb-4">
                <h1 className="text-xl font-bold text-gray-900 mb-2">
                  {selectedArticle.title}
                </h1>
                <p className="text-xs text-gray-500">Updated this week</p>
              </div>

              <div className="prose prose-sm max-w-none">
                {selectedArticle.content.split("\n\n").map((paragraph, idx) => {
                  if (paragraph.startsWith("**") && paragraph.includes(":**")) {
                    const headerEnd = paragraph.indexOf(":**") + 3;
                    const header = paragraph.slice(2, headerEnd - 3);
                    const rest = paragraph.slice(headerEnd).trim();
                    return (
                      <div key={idx} className="mb-4">
                        <h4 className="font-semibold text-gray-900 mb-1">
                          {header}
                        </h4>
                        {rest && <p className="text-gray-700">{rest}</p>}
                      </div>
                    );
                  }

                  if (paragraph.includes("\n- ")) {
                    const lines = paragraph.split("\n");
                    const title = lines[0];
                    const items = lines.slice(1);
                    return (
                      <div key={idx} className="mb-4">
                        {title && !title.startsWith("-") && (
                          <p className="text-gray-700 mb-2">{title}</p>
                        )}
                        <ul className="list-disc pl-5 space-y-1">
                          {items.map((item, itemIdx) => (
                            <li key={itemIdx} className="text-gray-700">
                              {item.replace(/^- /, "")}
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  }

                  if (/^\d+\.\s/.test(paragraph)) {
                    const items = paragraph.split("\n");
                    return (
                      <ol
                        key={idx}
                        className="list-decimal pl-5 space-y-1 mb-4"
                      >
                        {items.map((item, itemIdx) => (
                          <li key={itemIdx} className="text-gray-700">
                            {item.replace(/^\d+\.\s/, "")}
                          </li>
                        ))}
                      </ol>
                    );
                  }

                  return (
                    <p key={idx} className="text-gray-700 mb-4">
                      {paragraph}
                    </p>
                  );
                })}
              </div>

              {/* still need help section */}
              <div className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">
                  Still need help?
                </h4>
                <p className="text-sm text-gray-600 mb-3">
                  Can't find what you're looking for? Get in touch with our
                  support team.
                </p>
                <button
                  onClick={() => {
                    handleClose();
                    onContactClick?.();
                  }}
                  className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Contact Support
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HelpCentreModal;
